import { PartFinderReport, PartImageCandidate } from '@/api/generatedApi';
import { ImageMatchConfidencePill } from '@/components/PartFinder/confidence/ConfidencePill';
import { useCreateReportFromPartImageCandidate } from '@/hooks/api/part-finder-image-search/useCreateReportFromPartImageCandidate';
import { useGetPartImageIdentification } from '@/hooks/api/part-finder-image-search/useGetPartImageIdentification';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { ImageSearchOutlined } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { CollapsibleSection } from './CollapsibleSection';

const CandidateRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '8px 0',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

interface ImageSearchCandidateListProperties {
  identificationId: string;
  currentNiin?: string;
  candidates: PartImageCandidate[];
}

interface CandidateGroup {
  title?: string;
  candidates: PartImageCandidate[];
}

const groups = (candidates: PartImageCandidate[]): CandidateGroup[] => {
  const measured = candidates.filter(candidate => candidate.measured);
  if (measured.length === 0) return [{ candidates }];
  const examples = candidates.filter(candidate => !candidate.measured);
  return [
    { title: 'Matches for your measurements', candidates: measured },
    ...(examples.length > 0 ? [{ title: 'Suggested from the photo', candidates: examples }] : []),
  ];
};

const ImageSearchCandidateList = ({
  identificationId,
  currentNiin,
  candidates,
}: ImageSearchCandidateListProperties) => {
  const createReport = useCreateReportFromPartImageCandidate();
  const { showToast } = useToast();
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const runReport = (niin: string) =>
    createReport.mutate(
      { identificationId, niin },
      {
        onSuccess: report => {
          void navigate(getPath(`part-finder-reports/${report.id}`));
        },
        onError: () => showToast({ message: 'Could not start that report. Please try again.', type: 'error' }),
      }
    );

  const others = candidates.filter(candidate => candidate.niin !== currentNiin);
  return (
    <CollapsibleSection icon={<ImageSearchOutlined />} title='Other candidates from your photo search'>
      <Typography variant='body2' sx={{ marginBottom: '8px' }} data-testid='image-search-candidates-summary'>
        Your photo search found {candidates.length} candidates. Run a report on any of the others.
      </Typography>
      {groups(others).map(group => (
        <Box key={group.title ?? 'all'} data-testid='image-search-candidate-group'>
          {group.title && (
            <Typography variant='subtitle2' sx={{ marginTop: '8px', marginBottom: '4px' }}>
              {group.title}
            </Typography>
          )}
          {group.candidates.map(candidate => (
            <CandidateRow key={candidate.niin} data-testid='image-search-candidate'>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction='row' gap='8px' alignItems='center'>
                  <Typography variant='body2'>
                    {candidate.nsn} {candidate.itemName ?? ''}
                  </Typography>
                  <ImageMatchConfidencePill score={candidate.score} />
                </Stack>
                <Typography variant='caption' color='text.secondary' component='div'>
                  {candidate.reasons.join(' · ')}
                </Typography>
              </Box>
              <Button
                size='small'
                variant='outlined'
                disabled={createReport.isPending}
                onClick={() => runReport(candidate.niin)}
                sx={{ flexShrink: 0 }}
              >
                Run report
              </Button>
            </CandidateRow>
          ))}
        </Box>
      ))}
    </CollapsibleSection>
  );
};

export const ImageSearchCandidatesSection = ({ report }: { report: PartFinderReport }) => {
  const { data: identification } = useGetPartImageIdentification(report.imageIdentificationId);
  const candidates = identification?.candidates ?? [];
  const hasOthers = candidates.some(candidate => candidate.niin !== report.niin);

  return (
    <>
      {report.imageIdentificationId && hasOthers && (
        <ImageSearchCandidateList
          identificationId={report.imageIdentificationId}
          currentNiin={report.niin}
          candidates={candidates}
        />
      )}
    </>
  );
};
